import { useState, useEffect } from "react";
import { apiRequest } from "../../network/apiClient.js";
import { API_ENDPOINTS } from "../../api/apiEndpoints.js";
import { toast } from "react-toastify";
import { countryCodes, language_options } from '../Constants.jsx';

// ── Types ────────────────────────────────────────────────────────────────────

interface ContactData {
    contact_id: string | number;
    salutation: string;
    name: string;
    designation: string;
    email: string;
    mobile_country_code?: string;
    mobile_no: string;
    office_no?: string;
    reporting_to: string;
    race_of_pic?: string;
    language?: string;
}

interface EditableContact {
    salutation: string;
    name: string;
    designation: string;
    email: string;
    mobile_country_code: string;
    mobile_no: string;
    office_no: string;
    reporting_to: string;
    race_of_pic: string;
    race_of_pic_others: string;
    language: string[];
    contact_id?: string | number;
}

interface ParsedRace {
    race: string;
    raceOthers: string;
}

interface ContactsShowDetailProps {
    selectedContactModal: boolean;
    contactData: ContactData;
    setSelectedContactModal: (value: boolean) => void;
    onContactUpdated?: () => void;
    accountOwner: boolean;
    canViewLead: boolean;
}

const DEFAULT_CONTACT: EditableContact = {
    salutation: "MR.",
    name: "",
    designation: "",
    email: "",
    mobile_country_code: "+60",
    mobile_no: "",
    office_no: "",
    reporting_to: "No One",
    race_of_pic: "Malay",
    race_of_pic_others: "",
    language: ["English"],
};

// ── Component ────────────────────────────────────────────────────────────────

const ContactsShowDetail = ({
    selectedContactModal,
    contactData,
    setSelectedContactModal,
    onContactUpdated,
    accountOwner,
    canViewLead,
}: ContactsShowDetailProps) => {

    const canEdit = accountOwner || canViewLead;
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [showLanguageDropdown, setShowLanguageDropdown] = useState<boolean>(false);
    const [emailError, setEmailError] = useState<string>("");
    const [newContact, setNewContact] = useState<EditableContact>(DEFAULT_CONTACT);

    // ── Helpers ───────────────────────────────────────────────────────────────

    const parseRaceData = (raceString?: string): ParsedRace => {
        if (!raceString) return { race: "Malay", raceOthers: "" };

        const predefinedRaces = ["Malay", "Chinese", "Indian"];

        if (predefinedRaces.includes(raceString)) {
            return { race: raceString, raceOthers: "" };
        } else if (raceString.includes(' - ')) {
            const [mainRace, otherRace] = raceString.split(' - ');
            return { race: "Others", raceOthers: otherRace || "" };
        } else {
            return { race: raceString, raceOthers: "" };
        }
    };

    const buildContactState = (data: ContactData): EditableContact => {
        const { race, raceOthers } = parseRaceData(data?.race_of_pic);

        const rawMobile = data?.mobile_no || "";
        const countryCode = data?.mobile_country_code || "+60";
        const mobileNo = rawMobile.startsWith(countryCode)
            ? rawMobile.slice(countryCode.length)
            : rawMobile;

        return {
            salutation: data?.salutation || "MR.",
            name: data?.name || "",
            designation: data?.designation || "",
            email: data?.email || "",
            mobile_country_code: countryCode,
            mobile_no: mobileNo,
            office_no: data?.office_no || "",
            reporting_to: data?.reporting_to || "No One",
            race_of_pic: race,
            race_of_pic_others: raceOthers,
            language: data?.language
                ? data.language.split(", ").map((lang) => lang.trim())
                : ["English"],
        };
    };

    // ── Effects ───────────────────────────────────────────────────────────────

    useEffect(() => {
        if (contactData) {
            setNewContact(buildContactState(contactData));
        }
    }, [contactData]);

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;

        if (name === "email") {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            setEmailError(value && !emailRegex.test(value) ? "Please enter a valid email address" : "");
        }

        const sanitizedValue = name === "mobile_no"
            ? value.replace(/[\s-]/g, "").replace(/\D/g, "")  // strip non-digits too
            : value;

        setNewContact((prev) => ({ ...prev, [name]: sanitizedValue }));
    };

    const handleLanguageToggle = (lang: string, checked: boolean) => {
        setNewContact((prev) => ({
            ...prev,
            language: checked
                ? [...(prev.language ?? []), lang]
                : (prev.language ?? []).filter((l) => l !== lang),
        }));
    };

    const handleCancel = () => {
        setNewContact(buildContactState(contactData));
        setEmailError("");
        setIsEditing(false);
    };

    const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!newContact.email.trim()) {
            toast.error("Email is required");
            return;
        }

        if (!emailRegex.test(newContact.email)) {
            toast.error("Please enter a valid email address");
            setEmailError("Please enter a valid email address");
            return;
        }

        if (!newContact.mobile_no.trim()) {
            toast.error("Mobile number is required");
            return;
        }

        if (!/^\d+$/.test(newContact.mobile_no)) {
            toast.error("Mobile number must contain digits only");
            return;
        }

        if (newContact.mobile_no.length < 9) {
            toast.error("Mobile number must be at least 9 digits");
            return;
        }

        try {
            const payload = Object.fromEntries(
                Object.entries({
                    ...newContact,
                    contact_id: contactData.contact_id,
                    mobile_no: `${newContact.mobile_country_code}${newContact.mobile_no}`,
                }).map(([key, value]) => [
                    key,
                    typeof value === "string" && value.trim() === "" ? null : value,
                ])
            );

            const response = await apiRequest(
                `${import.meta.env.VITE_BASE_URL}/${API_ENDPOINTS.UPDATE_ACCOUNTS.UPDATE_CONTACTS}`,
                'POST',
                payload
            );

            if (response) {
                onContactUpdated?.();
                toast.success('Contact Successfully Updated');
            } else {
                toast.error("Error updating contact");
            }
        } catch (error) {
            toast.error("Error updating contact");
        } finally {
            setNewContact(buildContactState(contactData));
            setEmailError("");
            setIsEditing(false);
            setSelectedContactModal(false);
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────

    if (!selectedContactModal) return null;

    return (
        <div className="fixed inset-0 flex items-center justify-center z-50">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black opacity-50"
                onClick={() => {
                    setSelectedContactModal(false);
                    setIsEditing(false);
                }}
            />

            {/* Modal */}
            <div className="w-[65%] bg-white rounded-lg shadow-lg p-6 z-10">
                {/* Header */}
                <div className="w-full flex justify-center items-center mb-4 border-b">
                    <h2 className="text-xl font-bold mb-4">View Contact</h2>
                </div>

                {/* Form Grid */}
                <div className="grid grid-cols-2 gap-x-5 ml-3 text-[#475569]">

                    {/* Salutation */}
                    <div className="flex items-start mt-5">
                        <div className="border-b border-gray-200 pb-2 w-full flex">
                            <span className="w-1/2">Salutation</span>
                            {isEditing ? (
                                <select
                                    className="w-full bg-transparent focus:outline-none"
                                    name="salutation"
                                    value={newContact.salutation}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="MR.">MR.</option>
                                    <option value="MS.">MS.</option>
                                    <option value="MRS.">MRS.</option>
                                </select>
                            ) : (
                                <span className="w-full">{contactData.salutation}</span>
                            )}
                        </div>
                    </div>

                    {/* Name */}
                    <div className="flex items-start mt-5">
                        <div className="border-b border-gray-200 pb-2 w-full flex">
                            <span className="w-1/2">Name</span>
                            {isEditing ? (
                                <input
                                    type="text"
                                    className="w-full"
                                    name="name"
                                    placeholder={contactData.name}
                                    value={newContact.name}
                                    onChange={handleInputChange}
                                    required
                                />
                            ) : (
                                <span className="w-full">{contactData.name}</span>
                            )}
                        </div>
                    </div>

                    {/* Designation */}
                    <div className="flex items-start mt-5">
                        <div className="border-b border-gray-200 pb-2 w-full flex">
                            <span className="w-1/2">Designation</span>
                            {isEditing ? (
                                <input
                                    type="text"
                                    className="w-full"
                                    name="designation"
                                    placeholder={contactData.designation}
                                    value={newContact.designation}
                                    onChange={handleInputChange}
                                    required
                                />
                            ) : (
                                <span className="w-full">{contactData.designation}</span>
                            )}
                        </div>
                    </div>

                    {/* Email — with validation */}
                    <div className="flex items-start mt-5">
                        <div className="border-b border-gray-200 pb-2 w-full flex flex-col">
                            <div className="flex w-full">
                                <span className="w-1/2">Email</span>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        className="w-full"
                                        name="email"
                                        placeholder={contactData.email}
                                        value={newContact.email}
                                        onChange={handleInputChange}
                                        required
                                    />
                                ) : (
                                    <span className="w-full">{contactData.email}</span>
                                )}
                            </div>
                            {isEditing && emailError && (
                                <p className="text-red-500 text-xs mt-1 text-right">{emailError}</p>
                            )}
                        </div>
                    </div>

                    {/* Mobile Number — with country code */}
                    <div className="flex items-start mt-5">
                        <div className="border-b border-gray-200 pb-2 w-full flex">
                            <span className={isEditing ? "w-1/3 whitespace-nowrap" : "w-1/2 whitespace-nowrap"}>Mobile Number</span>
                            {isEditing ? (
                                <div className="flex flex-1 gap-2">
                                    <select
                                        name="mobile_country_code"
                                        value={newContact.mobile_country_code}
                                        onChange={handleInputChange}
                                        className="bg-transparent focus:outline-none text-md"
                                    >
                                        {countryCodes.map((code) => (
                                            <option key={code.value} value={code.value}>
                                                {code.value}
                                            </option>
                                        ))}
                                    </select>
                                    <input
                                        type="tel"
                                        className="flex-1 min-w-0"
                                        name="mobile_no"
                                        value={newContact.mobile_no}
                                        onChange={handleInputChange}
                                        placeholder="e.g. 123456789"
                                        required
                                    />
                                </div>
                            ) : (
                                <span className="w-full">{contactData.mobile_no}</span>
                            )}
                        </div>
                    </div>

                    {/* Office Number */}
                    <div className="flex items-start mt-5">
                        <div className="border-b border-gray-200 pb-2 w-full flex">
                            <span className="w-1/2 whitespace-nowrap">Office Number</span>
                            {isEditing ? (
                                <input
                                    type="text"
                                    className="w-full"
                                    name="office_no"
                                    placeholder={contactData.office_no}
                                    value={newContact.office_no}
                                    onChange={handleInputChange}
                                    required
                                />
                            ) : (
                                <span className="w-full">{contactData.office_no || '-'}</span>
                            )}
                        </div>
                    </div>

                    {/* Reporting To */}
                    <div className="flex items-start mt-5">
                        <div className="border-b border-gray-200 pb-2 w-full flex">
                            <span className="w-1/2">Reporting to</span>
                            {isEditing ? (
                                <select
                                    className="w-full"
                                    name="reporting_to"
                                    value={newContact.reporting_to}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="No One">No One</option>
                                    <option value="Owner / CEO / Director">Owner / CEO / Director</option>
                                    <option value="CFO / Finance Director / Finance Manager">CFO / Finance Director / Finance Manager</option>
                                    <option value="Partner">Partner</option>
                                </select>
                            ) : (
                                <span className="w-full">{contactData.reporting_to}</span>
                            )}
                        </div>
                    </div>

                    {/* Preferred Language */}
                    <div className="flex items-start mt-5">
                        <div className="border-b border-gray-200 pb-2 w-full flex">
                            <span className="w-1/2">Preferred Language</span>
                            {isEditing ? (
                                <div className="w-full relative">
                                    <div
                                        className="w-full rounded px-2 py-1 cursor-pointer bg-white flex justify-between items-center"
                                        onClick={() => setShowLanguageDropdown((prev) => !prev)}
                                    >
                                        <span>
                                            {newContact.language.length > 0
                                                ? newContact.language.join(", ")
                                                : "Select Language"}
                                        </span>
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="size-6">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
                                        </svg>
                                    </div>
                                    {showLanguageDropdown && (
                                        <div className="absolute top-full left-0 w-full bg-white border border-gray-200 rounded mt-1 z-10 shadow-lg">
                                            {language_options.map((lang) => (
                                                <label key={lang} className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        value={lang}
                                                        checked={newContact.language.includes(lang)}
                                                        onChange={(e) => handleLanguageToggle(lang, e.target.checked)}
                                                        className="mr-2"
                                                    />
                                                    {lang}
                                                </label>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <span className="w-full">{contactData.language}</span>
                            )}
                        </div>
                    </div>

                    {/* Race */}
                    <div className="flex items-start mt-5">
                        <div className="border-b border-gray-200 pb-2 w-full flex">
                            <span className="w-1/2">Race</span>
                            {isEditing ? (
                                <select
                                    className="w-full"
                                    name="race_of_pic"
                                    value={newContact.race_of_pic}
                                    onChange={handleInputChange}
                                    required
                                >
                                    <option value="Malay">Malay</option>
                                    <option value="Chinese">Chinese</option>
                                    <option value="Indian">Indian</option>
                                    <option value="Others">Others</option>
                                </select>
                            ) : (
                                <span className="w-full">
                                    {contactData?.race_of_pic?.includes(' - ')
                                        ? 'Others'
                                        : contactData?.race_of_pic || '-'}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Race — Others (editing) */}
                    {isEditing && newContact.race_of_pic === 'Others' && (
                        <div className="flex items-start mt-5">
                            <div className="border-b border-gray-200 pb-2 w-full flex">
                                <span className="w-1/2 whitespace-nowrap">If Others, Specify</span>
                                <input
                                    type="text"
                                    className="w-full"
                                    name="race_of_pic_others"
                                    value={newContact.race_of_pic_others}
                                    placeholder="Please specify"
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                        </div>
                    )}

                    {/* Race — Others (view) */}
                    {!isEditing && contactData?.race_of_pic?.includes(' - ') && (
                        <div className="flex items-start mt-5">
                            <div className="border-b border-gray-200 pb-2 w-full flex">
                                <span className="w-1/2 whitespace-nowrap">If Others, Specify</span>
                                <span className="w-full">{contactData.race_of_pic.split(' - ')[1] || '-'}</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer Buttons */}
                <div className="flex justify-end mt-6 gap-4">
                    {isEditing ? (
                        <>
                            <button
                                onClick={handleSubmit}
                                className="mt-4 bg-blue-500 text-white px-4 py-2 rounded"
                            >
                                Save
                            </button>
                            <button
                                onClick={handleCancel}
                                type="button"
                                style={{ backgroundColor: "#F9FAFB" }}
                                className="mt-4 border bg-white text-[#475569] px-4 py-2 rounded"
                            >
                                Cancel
                            </button>
                        </>
                    ) : (
                        <>
                            {canEdit && (
                                <button
                                    onClick={() => setIsEditing(true)}
                                    type="button"
                                    className="mt-4 bg-blue-500 text-white px-4 py-2 rounded"
                                >
                                    Edit
                                </button>
                            )}
                            <button
                                onClick={() => setSelectedContactModal(false)}
                                type="button"
                                style={{ backgroundColor: "#F9FAFB" }}
                                className="mt-4 border bg-white text-[#475569] px-4 py-2 rounded"
                            >
                                Dismiss
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ContactsShowDetail;